type IpeDebugEvent = {
  index: number;
  name: string;
  at: string;
  time: number;
  data?: any;
  stack?: string[];
};

type IpeDebugOptions = {
  stack?: boolean;
};

const MAX_EVENTS = 3000;

let enabled = true;
let counter = 0;
let events: IpeDebugEvent[] = [];

function nowIso(): string {
  return new Date().toISOString();
}

function safeArray(value: any): string[] {
  return Array.isArray(value) ? [...value] : [];
}

function sanitize(value: any, seen = new WeakSet<object>(), depth = 0): any {
  if (value == null) return value;

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }

  if (typeof value === 'function') {
    return '[Function]';
  }

  if (depth > 7) {
    return '[MaxDepth]';
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (value instanceof Map) {
    return Array.from(value.entries()).map(([k, v]) => [
      sanitize(k, seen, depth + 1),
      sanitize(v, seen, depth + 1),
    ]);
  }

  if (value instanceof Set) {
    return Array.from(value.values()).map(v => sanitize(v, seen, depth + 1));
  }

  if (Array.isArray(value)) {
    return value.map(v => sanitize(v, seen, depth + 1));
  }

  if (typeof value === 'object') {
    if (seen.has(value)) {
      return '[Circular]';
    }

    seen.add(value);

    const out: Record<string, any> = {};

    for (const key of Object.keys(value)) {
      out[key] = sanitize(value[key], seen, depth + 1);
    }

    return out;
  }

  return String(value);
}

function getStack(): string[] {
  const stack = new Error().stack ?? '';

  return stack
    .split('\n')
    .slice(3, 14)
    .map(x => x.trim())
    .filter(Boolean);
}

function countValues(values: string[]): Record<string, number> {
  const out: Record<string, number> = {};

  for (const value of values) {
    out[value] = (out[value] ?? 0) + 1;
  }

  return out;
}

function diffArrays(beforeValue: any, afterValue: any) {
  const before = safeArray(beforeValue);
  const after = safeArray(afterValue);

  const beforeCount = countValues(before);
  const afterCount = countValues(after);

  const added: string[] = [];
  const removed: string[] = [];

  for (const value of Object.keys(afterCount)) {
    const delta = afterCount[value] - (beforeCount[value] ?? 0);

    for (let i = 0; i < delta; i++) {
      added.push(value);
    }
  }

  for (const value of Object.keys(beforeCount)) {
    const delta = beforeCount[value] - (afterCount[value] ?? 0);

    for (let i = 0; i < delta; i++) {
      removed.push(value);
    }
  }

  const duplicates = Object.entries(afterCount)
    .filter(([, count]) => count > 1)
    .map(([value, count]) => ({ value, count }));

  return {
    beforeLength: before.length,
    afterLength: after.length,
    added,
    removed,
    duplicates,
  };
}

function itemSnapshot(item: any) {
  const element = item?.element ?? item ?? {};

  const activePlacements = safeArray(element?.activePlacements);
  const placements = safeArray(element?.placements);
  const originalPlacements = safeArray(element?.originalPlacements);

  return {
    uid: item?.uid ?? element?.uid ?? null,
    elementId: element?.id ?? null,
    code: element?.code ?? null,
    name: element?.name ?? null,
    type: element?.type ?? null,
    externalLink: element?.externalLink ?? null,

    counts: {
      activePlacements: activePlacements.length,
      placements: placements.length,
      originalPlacements: originalPlacements.length,
    },

    activePlacements,
    placements,
    originalPlacements,
  };
}

function event(name: string, data?: any, options: IpeDebugOptions = {}): void {
  if (!enabled) return;

  events.push({
    index: ++counter,
    name,
    at: nowIso(),
    time: Date.now(),
    data: sanitize(data),
    stack: options.stack ? getStack() : undefined,
  });

  if (events.length > MAX_EVENTS) {
    events = events.slice(events.length - MAX_EVENTS);
  }
}

export const IpeDebug = {
  enable(): void {
    enabled = true;
    event('debug.enabled');
  },

  disable(): void {
    event('debug.disabled');
    enabled = false;
  },

  clear(): void {
    events = [];
    counter = 0;
    event('debug.clear');
  },

  mark(label: string, data?: any): void {
    event('debug.mark', { label, data }, { stack: true });
  },

  event,

  dump(): string {
    return JSON.stringify(
      {
        generatedAt: nowIso(),
        count: events.length,
        events,
      },
      null,
      2
    );
  },

  last(count = 50): IpeDebugEvent[] {
    return events.slice(-count);
  },

  download(filename?: string): void {
    const name = filename ?? `ipe-debug-${Date.now()}.json`;
    const blob = new Blob([this.dump()], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();

    URL.revokeObjectURL(url);
  },

  array(value: any): string[] {
    return safeArray(value);
  },

  arrayDiff(before: any, after: any) {
    return diffArrays(before, after);
  },

  itemSnapshot,
};

(globalThis as any).IPE_DEBUG = IpeDebug;
